/**
 * Phase 3 chunk 4B: overlay-toggle service.
 *
 * The `§ overlays` topbar menu (chunk 4B) flips four globe overlays
 * on and off; settings persist to localStorage as `sat:overlays`.
 * The state shape is intentionally narrow — each overlay is a plain
 * boolean keyed by a stable string slug.
 *
 *   ribbons         — chunk-2 orbit ribbon for the selected satellite
 *   marquee         — chunk-3 3D shapes for marquee satellites
 *   stations        — chunk-4 ground-station overlay
 *   lightPollution  — chunk-5 VIIRS overlay (lazy-loaded raster)
 *
 * Layer code subscribes via {@link OverlayService.subscribe} and
 * shows/hides itself when the relevant flag changes.  Like
 * ObserverService, the constructor accepts injectable storage so
 * vitest can swap a fake.
 */
export type OverlayKey =
  | 'ribbons' | 'marquee' | 'stations' | 'lightPollution' | 'aurora'
  // Phase 9 chunk 2 — GNSS constellation color layers. Default off
  // (see docs/phase9.md § II row 4) so the existing cyan PAYLOAD
  // story doesn't change for users on first load.
  | 'gnssGps' | 'gnssGalileo' | 'gnssGlonass' | 'gnssBeidou'
  // Phase 9 chunk 3 — sun-synchronous Earth-observation ribbons.
  // Default off — 9 simultaneous polylines at ±0.5 orbit each is
  // cheap but not free, and the always-on visual would distract
  // first-time users who haven't asked for it.
  | 'sunSynchRibbons'
  // Phase 9 chunk 4 — historical decay traces (fading red ground
  // tracks for the last 30 days of predicted reentries). Default off
  // — data sparsity means rendering nothing is the common case.
  | 'decayTrails'
  // Phase 10 chunk 3 — amateur SatNOGS ground stations overlay.
  // Default off — the ~3,000 amateur stations would crowd the
  // existing 41-station curated catalog on first load. Lazy-fetches
  // /api/v1/amateur-stations on first flip-to-true.
  | 'amateurStations'
  // Post-Phase-11 — major constellation stick-figure overlay with
  // labels (Orion, Big Dipper, Cassiopeia, …). Default off — the
  // unadorned BSC5 starfield is the default look; constellations
  // are a "show me where the patterns are" toggle.
  | 'constellations'
  // Post-Phase-11 — sky-view pass-arc layer (rise/peak/set polylines
  // for the marquee roster + RCS-estimated bright candidates).
  // Default ON — the arcs are sky view's headline feature; the
  // toggle exists so a user can suppress them when the bright-pass
  // discovery surfaces too many candidates for the night they care
  // about. Only takes effect in sky view (no globe-view rendering).
  | 'passArcs';

export type OverlayState = Record<OverlayKey, boolean>;

const STORAGE_KEY = 'sat:overlays';

const DEFAULT_STATE: OverlayState = {
  ribbons: true,
  marquee: true,
  stations: false,
  lightPollution: false,
  aurora: false,
  gnssGps:     false,
  gnssGalileo: false,
  gnssGlonass: false,
  gnssBeidou:  false,
  sunSynchRibbons: false,
  decayTrails: false,
  amateurStations: false,
  constellations: false,
  passArcs: true,
};

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class OverlayService {
  private state: OverlayState;
  private listeners = new Set<(s: OverlayState) => void>();
  /** Phase 7 chunk 2A — keys whose visibility is temporarily forced to
   *  off (sky view's auto-hide of surface-irrelevant overlays). When
   *  non-null, `setEnabled` on a suspended key is a no-op and the
   *  observed `state` reports `false` for it. The pre-suspension value
   *  is captured here so {@link restore} can put it back exactly. */
  private suspendedValues: Partial<Record<OverlayKey, boolean>> | null = null;

  constructor(private readonly storage: StorageLike = globalThis.localStorage) {
    this.state = this.loadFromStorage();
  }

  /** Returns a read-only copy of the current state. */
  current(): OverlayState {
    return { ...this.state };
  }

  isEnabled(key: OverlayKey): boolean {
    return this.state[key];
  }

  setEnabled(key: OverlayKey, enabled: boolean): void {
    // Suspended keys are write-locked — `enterSkyView` blanked them and
    // the user's only path to changing them is to exit sky view first.
    // Silently dropping the call keeps the OverlaysMenu UI logic simple
    // (it can fire a click handler without first checking suspended).
    if (this.isSuspended(key)) return;
    if (this.state[key] === enabled) return;
    this.state = { ...this.state, [key]: enabled };
    this.persist();
    this.notify();
  }

  /** Subscribe to changes.  Sync-emits the current state on subscribe. */
  subscribe(listener: (state: OverlayState) => void): () => void {
    this.listeners.add(listener);
    listener(this.current());
    return () => this.listeners.delete(listener);
  }

  // ─── Phase 7 chunk 2A: sky-view suspend/restore ────────────────────────

  /**
   * Temporarily force the given overlay keys to `false`, capturing
   * their prior values so {@link restore} can put them back exactly.
   * Skipped silently if a suspension is already active (no nested
   * suspends; the caller is expected to balance enter/exit cleanly).
   *
   * The on-disk `localStorage` state is **not** modified — suspension
   * is a runtime visibility veto, not a user preference change.
   */
  suspend(keys: readonly OverlayKey[]): void {
    if (this.suspendedValues !== null) return;
    if (keys.length === 0) return;
    const captured: Partial<Record<OverlayKey, boolean>> = {};
    const next: OverlayState = { ...this.state };
    for (const key of keys) {
      captured[key] = this.state[key];
      next[key] = false;
    }
    this.suspendedValues = captured;
    this.state = next;
    // Always notify — even when no boolean flipped, subscribers may
    // need to re-read isSuspended() to render the lock-state UI cue
    // (OverlaysMenu uses this to mark rows as read-only in sky view).
    // Suspension is runtime-only; do NOT persist to localStorage.
    this.notify();
  }

  /**
   * Restore the values captured by the prior {@link suspend} call,
   * clearing the suspension and notifying subscribers. No-op when
   * nothing is suspended.
   */
  restore(): void {
    if (this.suspendedValues === null) return;
    const captured = this.suspendedValues;
    this.suspendedValues = null;
    const next: OverlayState = { ...this.state };
    for (const key of Object.keys(captured) as OverlayKey[]) {
      const prior = captured[key];
      if (prior !== undefined) {
        next[key] = prior;
      }
    }
    this.state = next;
    // Always notify so UI cues track the cleared-suspension state.
    this.notify();
  }

  /** True iff the given key is currently suspended (its visibility
   *  forced to false by an active suspend call). */
  isSuspended(key: OverlayKey): boolean {
    return this.suspendedValues !== null && key in this.suspendedValues;
  }

  /** Snapshot of all suspended keys. Empty array when nothing is
   *  suspended. Used by the OverlaysMenu UI to render lock-state cues. */
  suspendedKeys(): readonly OverlayKey[] {
    if (this.suspendedValues === null) return [];
    return Object.keys(this.suspendedValues) as OverlayKey[];
  }

  // ─── Internals ─────────────────────────────────────────────────────────

  private loadFromStorage(): OverlayState {
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (raw === null) return { ...DEFAULT_STATE };
      const parsed = JSON.parse(raw) as Partial<OverlayState>;
      if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_STATE };
      // Merge defaults so we silently absorb a future overlay being added.
      return {
        ribbons:        typeof parsed.ribbons        === 'boolean' ? parsed.ribbons        : DEFAULT_STATE.ribbons,
        marquee:        typeof parsed.marquee        === 'boolean' ? parsed.marquee        : DEFAULT_STATE.marquee,
        stations:       typeof parsed.stations       === 'boolean' ? parsed.stations       : DEFAULT_STATE.stations,
        lightPollution: typeof parsed.lightPollution === 'boolean' ? parsed.lightPollution : DEFAULT_STATE.lightPollution,
        aurora:         typeof parsed.aurora         === 'boolean' ? parsed.aurora         : DEFAULT_STATE.aurora,
        gnssGps:        typeof parsed.gnssGps        === 'boolean' ? parsed.gnssGps        : DEFAULT_STATE.gnssGps,
        gnssGalileo:    typeof parsed.gnssGalileo    === 'boolean' ? parsed.gnssGalileo    : DEFAULT_STATE.gnssGalileo,
        gnssGlonass:    typeof parsed.gnssGlonass    === 'boolean' ? parsed.gnssGlonass    : DEFAULT_STATE.gnssGlonass,
        gnssBeidou:     typeof parsed.gnssBeidou     === 'boolean' ? parsed.gnssBeidou     : DEFAULT_STATE.gnssBeidou,
        sunSynchRibbons: typeof parsed.sunSynchRibbons === 'boolean' ? parsed.sunSynchRibbons : DEFAULT_STATE.sunSynchRibbons,
        decayTrails:     typeof parsed.decayTrails     === 'boolean' ? parsed.decayTrails     : DEFAULT_STATE.decayTrails,
        amateurStations: typeof parsed.amateurStations === 'boolean' ? parsed.amateurStations : DEFAULT_STATE.amateurStations,
        constellations:  typeof parsed.constellations  === 'boolean' ? parsed.constellations  : DEFAULT_STATE.constellations,
        passArcs:        typeof parsed.passArcs        === 'boolean' ? parsed.passArcs        : DEFAULT_STATE.passArcs,
      };
    } catch {
      return { ...DEFAULT_STATE };
    }
  }

  private persist(): void {
    this.storage.setItem(STORAGE_KEY, JSON.stringify(this.state));
  }

  private notify(): void {
    const snapshot = this.current();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }
}

// Singleton accessor — UI + Globe share one instance; tests build their own.
let _shared: OverlayService | null = null;

export function getSharedOverlays(): OverlayService {
  if (_shared === null) {
    _shared = new OverlayService();
  }
  return _shared;
}

/** Test-only: reset between specs. */
export function __resetSharedOverlaysForTests(): void {
  _shared = null;
}
